import styled, { css } from 'styled-components';
import questionBackground from '../../assets/backgrounds/pattern.webp';
import { fadeIn, fadeOut } from '../../utils/Animations';
import { onVerticalScreen } from '../../utils/css';

export const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: ${({ theme }) => theme.fonts.citiSans};
  background: url(${questionBackground}), ${({ theme }) => theme.colors.background};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;

  .fade-in {
    animation: ${fadeIn} 0.5s;
    animation-fill-mode: forwards;
  }

  .fade-out {
    animation: ${fadeOut} 0.5s;
    animation-delay: 2.5s;
    animation-fill-mode: forwards;
  }
`;

export const TopBar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: 100%;
  padding: ${({ theme }) => theme.getValue(8)} ${({ theme }) => theme.getValue(16)};
  box-sizing: border-box;
`;

// export const CategoryLabel = styled.p`
//   margin: 0;
//   justify-self: start;
//   font-weight: 700;
//   color: ${({ theme }) => theme.colors.white};
//   font-size: ${({ theme }) => theme.getFontSize(20)};
// `;

export const ProgressLabel = styled.p`
  grid-column: 2;
  margin: 0;
  justify-self: center;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.getFontSize(20)};
  text-align: center;
`;

export const TimerSlot = styled.div`
  grid-column: 3;
  justify-self: end;
  display: flex;
  align-items: center;
`;

export const CountdownWrapper = styled.div`
  margin-top: ${({ theme }) => theme.getValue(32)};
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
  ${({ theme }) =>
    onVerticalScreen(css`
      margin-top: ${theme.getValue(64)};
    `)}
`;

/* Prior-year left chrome (timer + difficulty) — kept for easy restore */
export const LeftColumn = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  padding: ${({ theme }) => theme.getValue(8)};
  & p {
    font-weight: 700;
    color: ${({ theme }) => theme.colors.white};
    font-size: ${({ theme }) => `${theme.getFontSize(20)}`};
    margin: 0;
  }
`;

export const RightColumn = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
`;

export const Header = styled.div`
  box-sizing: border-box;
  padding: ${({ theme }) => theme.getValue(30)};
  display: flex;
  justify-content: center;
  flex-direction: column;
  & > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    &:nth-child(2) {
      margin-top: ${({ theme }) => theme.getFontSize(24)};
    }
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.gray};
    font-size: ${({ theme }) => theme.getFontSize(20)};
    line-height: ${({ theme }) => theme.getFontSize(22)};
    font-weight: 700;
  }
  h2 {
    font-weight: 700;
    margin: 0;
    color: ${({ theme }) => theme.colors.vividBlue};
    font-size: ${({ theme }) => theme.getFontSize(30)};
    line-height: ${({ theme }) => theme.getFontSize(33)};
  }
`;

export const Bottom = styled.div`
  padding-inline: ${({ theme }) => theme.getValue(16)};
  padding-top: ${({ theme }) => theme.getValue(20)};
  padding-bottom: ${({ theme }) => theme.getValue(28)};
  display: flex;
  width: 100%;
  & > div {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.vividBlue};

    p {
      margin: 0;
      font-weight: 700;
      font-size: ${({ theme }) => theme.getFontSize(20)};
    }

    &:first-child {
      justify-content: flex-start;
      color: ${({ theme }) => theme.colors.white};
    }
    &:last-child {
      justify-content: flex-end;
      gap: ${({ theme }) => theme.getValue(12)};
      color: ${({ theme }) => theme.colors.white};
      margin-right: ${({ theme }) => theme.getValue(78)};

      & > a {
        padding-inline: ${({ theme }) => theme.getValue(10)};
        text-transform: uppercase;
        display: block;
        text-align: right;
        font-size: ${({ theme }) => theme.getFontSize(15)};
        letter-spacing: ${({ theme }) => theme.getValue(0.8333)};
        font-weight: 700;
        white-space: nowrap;
        cursor: pointer;
      }
    }
  }
`;

export const SkipQuestionButton = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.vividBlue};
  color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fonts.citiSans};
  font-weight: 700;
  font-size: ${({ theme }) => theme.getFontSize(15)};
  line-height: 1;
  white-space: nowrap;
  padding: ${({ theme }) => theme.getValue(8)} ${({ theme }) => theme.getValue(16)};
  border-radius: ${({ theme }) => theme.getValue(999)};
`;

export const QuestionText = styled.h1`
  text-align: center;
  font-size: ${({ theme }) => theme.getFontSize(32)};
  line-height: ${({ theme }) => theme.getFontSize(35)};
  font-weight: 700;
  margin: 0;
  margin-bottom: ${({ theme }) => theme.getValue(24)};
  color: ${({ theme }) => theme.colors.white};
  padding-inline: ${({ theme }) => theme.getValue(120)};
  min-height: ${({ theme }) => theme.getValue(90)};
`;

export const NextQuestion = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.getValue(32)};
  color: ${({ theme }) => theme.colors.white};
  min-height: ${({ theme }) => theme.getValue(90)};
  margin-bottom: ${({ theme }) => theme.getValue(24)};
  & > div {
    margin: 0;
    width: initial;
  }

  h1 {
    margin: 0;
    font-weight: 700;
  }
`;

export const QuestionsCompleted = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.getValue(16)};
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.getValue(24)};
  min-height: ${({ theme }) => theme.getValue(90)};

  h1,
  h3 {
    margin: 0;
    font-weight: 700;
  }
`;

/* Prior-year timer chrome — kept for easy restore */
export const TimerContainer = styled.div`
  position: relative;
  img {
    height: ${({ theme }) => theme.getValue(26)};
    width: ${({ theme }) => theme.getValue(100)};
  }
  svg {
    height: ${({ theme }) => theme.getValue(28)};
    width: ${({ theme }) => theme.getValue(28)};
  }
  & > p {
    position: absolute;
    width: max-content;
    top: calc(100% + ${({ theme }) => theme.getValue(8)});
    left: 0;
    color: ${({ theme }) => theme.colors.green};
    font-weight: 700;
    font-size: ${({ theme }) => theme.getFontSize(13)};
    & > span {
      text-transform: uppercase;
    }
  }
`;
